import {
  isGuestWorkspaceRole,
  useCreateAssetShareLink,
  useCurrentAccount,
  useRoles,
} from '@gripp/shared-logic';
import { Colors, tokens } from '@gripp/shared-ui';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';
import QRCode from 'react-native-qrcode-svg';
import { CopyText } from '../../shared/components/copyText';

export type ShareAssetPanelProps = {
  workspaceAssetId: string;
  /** The asset's own workspace — need not be the caller's active one. */
  workspaceId?: string;
  /** Withholds both the gate and the mint until the band itself is open. */
  enabled?: boolean;
  testID?: string;
};

/**
 * Mirrors the resolver's `holdsWorkspacePermission('account.invite', ...)`
 * gate: minting requires `account.invite` in the ASSET'S workspace (every
 * role but Guest holds it explicitly; Global Admin / the function account
 * reach it through the unscoped `*.*` wildcard instead). Fails CLOSED —
 * unresolved account/role data reads as "cannot share", never as "can".
 *
 * Exported so `assetChatPanel.tsx` can decide, above this panel, whether the
 * header kebab's menu has anything to offer at all — `ShareAssetPanel` is
 * the only item, and a menu whose sole item is hidden must not open empty.
 * One predicate, read in both places, rather than two copies that could
 * drift out of sync with the resolver or each other.
 */
export const useCanShareAsset = (workspaceId?: string) => {
  const { account, isLoading: isAccountLoading } = useCurrentAccount();
  const { roles, isLoading: isRolesLoading } = useRoles();
  const isLoading = isAccountLoading || isRolesLoading;
  const canShare =
    !isLoading &&
    !!workspaceId &&
    (account?.isGlobalAdmin() ||
      !isGuestWorkspaceRole(workspaceId, account?.workspaceRoles, roles));
  return { canShare, isLoading };
};

/**
 * Share, at the top of the header-kebab band (task 3.7) — always visible
 * once the gate below passes, above `ParticipantsDisclosure`.
 *
 * Reuses `shareIssuePanel`'s treatment (QR, copyable URL, expiry line) as a
 * SIBLING rather than a shared component with it. That panel is a
 * dismissible Portal/Modal a feed row triggers for one activity; this one is
 * a permanently-mounted section of an already-open inline band, mints for
 * the whole asset, and gates on a different permission (`account.invite`)
 * than "can you see this Issue". The two would need to fork on visibility,
 * layout and gate the moment either changed, so a shared component would
 * cost more in conditional plumbing than the handful of shared styles buy —
 * the same call gripp-3vt.5 made for the hooks underneath both panels.
 *
 * Reads its own permission gate via `useCanShareAsset` rather than taking it
 * as a prop, mirroring `AssetChatHeaderButton`'s pattern of owning its own
 * gate.
 */
export const ShareAssetPanel = ({
  workspaceAssetId,
  workspaceId,
  enabled = true,
  testID,
}: ShareAssetPanelProps) => {
  const { t } = useTranslation();
  const { canShare: canShareAsset } = useCanShareAsset(workspaceId);
  const { mutateAsync, isPending } = useCreateAssetShareLink();
  const [url, setUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState(false);

  // Mints on every open rather than once ever — createShareLink is
  // idempotent per subject, so re-minting on a later open just returns the
  // still-active link (or a fresh one, if the last had expired).
  useEffect(() => {
    if (!enabled || !canShareAsset) {
      setUrl(null);
      setExpiresAt(null);
      setError(false);
      return;
    }
    let cancelled = false;
    const mint = async () => {
      try {
        const result = await mutateAsync({ subjectId: workspaceAssetId });
        if (cancelled) return;
        const link = result?.share?.createShareLink;
        if (!link?.token) {
          setError(true);
          return;
        }
        setUrl(`${window.location.origin}/share/${link.token}`);
        setExpiresAt(link.expiresAt ?? null);
      } catch {
        if (!cancelled) setError(true);
      }
    };
    mint();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, canShareAsset, workspaceAssetId]);

  if (!canShareAsset) return null;

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.description}>{t('chat.share.description')}</Text>

      {isPending && !url && (
        <ActivityIndicator style={styles.loader} color={Colors.primaryDark} />
      )}

      {error && <Text style={styles.error}>{t('chat.share.error')}</Text>}

      {url && (
        <View style={styles.body}>
          <View style={styles.qr}>
            <QRCode value={url} size={120} />
          </View>
          <Text style={styles.url} numberOfLines={2}>
            {url}
          </Text>
          <CopyText
            textToCopy={url}
            buttonText={t('chat.share.copyLink')}
            successMessage={t('chat.share.copied')}
            errorMessage={t('chat.share.copyError')}
          />
          {expiresAt && (
            // Same key shareIssuePanel uses for its own expiry line — the
            // wording applies unchanged to an asset-scoped link.
            <Text style={styles.expiry}>
              {t('share.expiresOn', { date: moment(expiresAt).format('LL') })}
            </Text>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: tokens.spacing.sm,
    paddingBottom: tokens.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: Colors.role.borderSubtle,
  },
  description: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSecondary,
  },
  loader: {
    marginVertical: tokens.spacing.md,
  },
  error: {
    ...tokens.typography.label.medium,
    color: Colors.semantic.error,
  },
  body: {
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  qr: {
    padding: tokens.spacing.md,
    backgroundColor: Colors.white,
  },
  url: {
    ...tokens.typography.label.small,
    color: Colors.role.textSecondary,
    textAlign: 'center',
  },
  expiry: {
    ...tokens.typography.label.small,
    color: Colors.role.textSecondary,
    textAlign: 'center',
  },
});
